import React, { useCallback, forwardRef, useEffect, useRef } from "react"
import TextareaAutosize from "react-textarea-autosize"
import { cn } from "../../styles/utils"
import { FormControl } from "../gluestack/form-control"
import { FieldError } from "./FieldError"
import { FieldLabel } from "./FieldLabel"
import type { TextAreaProps } from "./TextArea.types"
import { textFieldBase, textFieldInputBase } from "./TextInput"
import { useDelayedInputHandler } from "./hooks"

const sizeMap = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl",
}

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextAreaProps & {
    size?: "sm" | "md" | "lg" | "xl"
    variant?: "default"
  }
>((props, ref) => {
  const {
    value,
    handleChange,
    className,
    textAreaContainerClassName,
    textAreaClassName,
    hideError,
    minRows = 1,
    maxRows,
    placeholder,
    disabled,
    label,
    required,
    error,
    autoResize = true,
    autofocus = false,
    size = "md",
    // variant = "default", // not used anymore
  } = props

  const internalRef = useRef<HTMLTextAreaElement>(null)

  const { value: internalValue, handleChange: delayedHandleChange } =
    useDelayedInputHandler({
      handleChange,
      value: value || "",
    })

  // Handle autofocus
  useEffect(() => {
    if (autofocus && internalRef.current) {
      internalRef.current.focus()
    }
  }, [autofocus])

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      delayedHandleChange(e.target.value)
    },
    [delayedHandleChange],
  )

  const inputClass = cn(
    textFieldBase,
    textFieldInputBase,
    sizeMap[size],
    textAreaClassName,
  )

  return (
    <FormControl
      className={className}
      isInvalid={!!error}
      isRequired={required}
      isDisabled={disabled}
    >
      <FieldLabel label={label} required={required} />
      <div className={textAreaContainerClassName}>
        <TextareaAutosize
          ref={(tag) => {
            // Update internal ref for autofocus
            if (internalRef.current !== tag) {
              ;(internalRef as any).current = tag
            }
            // Handle external ref
            if (typeof ref === "function") {
              ref(tag)
            } else if (ref) {
              ref.current = tag
            }
          }}
          className={inputClass}
          value={internalValue}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          minRows={autoResize ? minRows : undefined}
          maxRows={autoResize ? maxRows : undefined}
        />
      </div>
      {!hideError && <FieldError error={error} />}
    </FormControl>
  )
})
